import { PropertyList } from '@app/components'
import { sugarDateTime } from '@app/lib'
import { type SugarPaymentSetupConfirmable, type SugarPaymentSetupViewResponse } from '@app/types'
import { Alert, Box, Button, Group, LoadingOverlay, Stack, Text } from '@mantine/core'
import { IconAlertCircle, IconCheck, IconClock, IconPencil } from '@tabler/icons-react'
import { type UseQueryResult } from '@tanstack/react-query'
import { isEmpty } from 'lodash'

import { type PaymentSetupConfig, toPaymentSetupItems } from './paymentSetupConfig'

export interface PaymentSetupPanelProps<T extends SugarPaymentSetupConfirmable> {
  config: PaymentSetupConfig<T>
  onConfirm: () => void
  onUpdate: () => void
  query: UseQueryResult<SugarPaymentSetupViewResponse<T>>
}

// Presentational only — the query is a prop (Task 4 hoists it to the page so both records are known
// at first paint, which the tab alert icons depend on), and there is no checkbox/confirm mutation here:
// Confirm just calls onConfirm to open the confirm modal (Task 3).
export const PaymentSetupPanel = <T extends SugarPaymentSetupConfirmable>({
  config,
  onConfirm,
  onUpdate,
  query
}: PaymentSetupPanelProps<T>) => {
  const { data, isFetching, isLoadingError, isPending, refetch } = query
  const record = data?.data ?? null
  const pending = data?.pendingSubmission
  // Status/date only. `record` stays the approved details until staff approve the new submission.
  const pendingAlert = pending ? (
    <Alert
      color='blue'
      icon={<IconClock />}
    >
      Submitted {sugarDateTime(pending.submittedDate)}, awaiting review.
    </Alert>
  ) : null

  return (
    <Box pos='relative'>
      <LoadingOverlay visible={isFetching} />
      <Stack
        mih={200}
        pt='lg'
        spacing='lg'
      >
        {isPending ? null : isLoadingError ? (
          <Stack
            align='start'
            spacing='sm'
          >
            <Alert
              color='red.6'
              icon={<IconAlertCircle />}
            >
              We could not load this information right now.
            </Alert>
            <Button
              onClick={() => refetch()}
              variant='default'
            >
              Try again
            </Button>
          </Stack>
        ) : !data?.success ? (
          <Alert
            color='red.6'
            icon={<IconAlertCircle />}
          >
            {isEmpty(data?.messages) ? 'We could not load this information right now.' : data?.messages?.[0]?.message}
          </Alert>
        ) : !record && pendingAlert ? (
          <>
            {pendingAlert}
            <Group position='right'>
              <Button
                leftIcon={<IconPencil size={18} />}
                onClick={onUpdate}
                variant='default'
              >
                Update
              </Button>
            </Group>
          </>
        ) : !record ? (
          <Stack
            align='center'
            p='xl'
            spacing='sm'
          >
            <Text
              color='dimmed'
              size='xl'
            >
              {config.emptyTitle}
            </Text>
            <Text
              align='center'
              color='dimmed'
              size='sm'
            >
              {config.emptyDescription}
            </Text>
            <Button
              mt='sm'
              onClick={onUpdate}
            >
              {config.emptyCta}
            </Button>
          </Stack>
        ) : (
          <>
            {pendingAlert}
            <PropertyList items={toPaymentSetupItems(config, record)} />
            <Group position='right'>
              <Button
                leftIcon={<IconPencil size={18} />}
                onClick={onUpdate}
                variant='default'
              >
                Update
              </Button>
              <Button
                leftIcon={<IconCheck size={18} />}
                onClick={onConfirm}
              >
                {record.confirmedDate ? 'Confirm again' : 'Confirm'}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Box>
  )
}
